import { FC, Fragment, useReducer } from 'react';

import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { Download } from 'lucide-react';
import { isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DatePicker } from '@mui/x-date-pickers';
import { PickerValue } from '@mui/x-date-pickers/internals';

import { ExportStoreHistoryForm, initialValues } from './form';

import { storeService } from 'v2/api/storeService';
import { handleHttpError } from 'v2/utils';

export const ExportStoreHistoryButton: FC = () => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger, isMutating } = useSWRMutation('exportStoreHistory', storeService.exportStoreHistory, {
    onSuccess: () => {
      handleClose();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<ExportStoreHistoryForm>({
    initialValues,
    onSubmit: (values) => {
      if (isNullish(values.startDate) || isNullish(values.endDate)) {
        toast.error('Please specify the start/end date');
      } else {
        trigger({ startDate: values.startDate.format(), endDate: values.endDate.format() });
      }
    },
  });

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  const handleChangeDate = (field: keyof ExportStoreHistoryForm) => (value: PickerValue) => {
    formik.setFieldValue(field, value);
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<Download width={20} height={20} />} variant="contained" size="large">
        Export
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Export Store History</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent className="form-group">
            <DatePicker
              value={formik.values.startDate}
              onChange={handleChangeDate('startDate')}
              label="Start Date"
              format="DD.MM.YYYY"
            />

            <DatePicker
              value={formik.values.endDate}
              onChange={handleChangeDate('endDate')}
              maxDate={dayjs()}
              label="End Date"
              format="DD.MM.YYYY"
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button loading={isMutating} type="submit" variant="contained">
              Export
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
